import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  BookOpen,
  CheckCircle2,
  Circle,
  Loader2,
  RefreshCw,
  AlertCircle,
  User,
  Mail,
  GraduationCap,
  MapPin,
  Calendar,
  Hash,
  Star,
} from "lucide-react";
import {
  useAdminGetApplicationCoursesQuery,
  useAdminRegisterApplicationCourseMutation,
  useAdminUnregisterApplicationCourseMutation,
} from "../../api/examsApi";

function safeMsg(err: any) {
  return err?.data?.message || err?.error || err?.message || "Something went wrong";
}

const APP_STATUS_COLORS: Record<string, string> = {
  draft:     "bg-gray-100 text-gray-500",
  submitted: "bg-blue-50 text-blue-700",
  paid:      "bg-cyan-50 text-cyan-700",
  approved:  "bg-emerald-50 text-emerald-700",
  rejected:  "bg-rose-50 text-rose-700",
  completed: "bg-indigo-50 text-indigo-700",
};

export default function AdminApplicationCoursesPage() {
  const { id: appId } = useParams<{ id: string }>();
  const [toggling, setToggling] = useState<number | null>(null);
  const [flashError, setFlashError] = useState<string | null>(null);

  const { data, isLoading, isError, refetch, isFetching } =
    useAdminGetApplicationCoursesQuery(appId!, { skip: !appId });

  const [register] = useAdminRegisterApplicationCourseMutation();
  const [unregister] = useAdminUnregisterApplicationCourseMutation();

  const application = data?.application ?? null;
  const courses: any[] = data?.courses ?? [];

  async function toggle(course: any) {
    if (!appId || toggling !== null) return;
    setToggling(course.id);
    setFlashError(null);
    try {
      if (course.is_registered) {
        await unregister({ appId: appId!, courseId: course.id }).unwrap();
      } else {
        await register({ appId: appId!, course_id: course.id }).unwrap();
      }
    } catch (e) {
      setFlashError(safeMsg(e));
    } finally {
      setToggling(null);
    }
  }

  const registered = courses.filter((c) => c.is_registered);
  const unregistered = courses.filter((c) => !c.is_registered);

  return (
    <div className="space-y-6 max-w-4xl mx-auto p-4 md:p-6 pb-20">

      {/* Header */}
      <div className="bg-white border border-gray-100 rounded-[2.5rem] p-8 shadow-sm">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-6">
            <div className="w-16 h-16 rounded-2xl bg-gray-900 flex items-center justify-center text-white shadow-xl flex-shrink-0">
              <BookOpen className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-black text-gray-900 tracking-tight leading-none uppercase">
                Course Registration
              </h1>
              <p className="text-gray-400 text-sm mt-1.5 font-medium">
                Manage which courses this candidate is sitting
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {application && (
              <Link
                to={`/exams/admin/applications/${appId}/course-grading`}
                className="px-5 py-2.5 rounded-xl border bg-gray-900 text-white hover:bg-indigo-600 text-[10px] font-black uppercase tracking-widest transition-all shadow-sm flex items-center gap-2"
              >
                <Star className="w-3 h-3" /> Grade Courses
              </Link>
            )}
            <button
              onClick={() => refetch()}
              className="p-3 rounded-xl border bg-white hover:bg-gray-50 transition-all active:scale-95 shadow-sm"
            >
              <RefreshCw
                className={`w-4 h-4 text-indigo-500 ${isFetching ? "animate-spin" : ""}`}
              />
            </button>
            <Link
              to={-1 as any}
              className="p-3 rounded-xl border bg-white hover:bg-gray-50 transition-all shadow-sm"
            >
              <ArrowLeft className="w-4 h-4 text-gray-500" />
            </Link>
          </div>
        </div>
      </div>

      {/* Error flash */}
      {flashError && (
        <div className="bg-rose-50 border border-rose-100 rounded-2xl px-6 py-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-rose-400 flex-shrink-0" />
          <p className="text-sm font-bold text-rose-600">{flashError}</p>
        </div>
      )}

      {isLoading ? (
        <div className="bg-white border border-gray-100 rounded-[2.5rem] p-20 flex flex-col items-center justify-center">
          <Loader2 className="w-10 h-10 text-indigo-500 animate-spin mb-4" />
          <span className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-400">
            Loading…
          </span>
        </div>
      ) : isError ? (
        <div className="bg-white border border-gray-100 rounded-[2.5rem] p-20 text-center">
          <AlertCircle className="w-12 h-12 mx-auto text-rose-200 mb-4" />
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-300">
            Failed to load courses
          </p>
          <button
            onClick={() => refetch()}
            className="mt-6 px-6 py-2.5 rounded-xl bg-gray-900 text-white text-[10px] font-black uppercase tracking-widest"
          >
            Retry
          </button>
        </div>
      ) : (
        <div className="space-y-6">

          {/* Applicant Info Card */}
          {application && (
            <div className="bg-white border border-gray-100 rounded-[2rem] overflow-hidden shadow-sm">
              <div className="px-8 py-5 border-b border-gray-50 bg-gray-50/50 flex items-center justify-between">
                <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  Applicant Details
                </div>
                {application.status && (
                  <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${APP_STATUS_COLORS[application.status] ?? "bg-gray-100 text-gray-500"}`}>
                    {application.status}
                  </span>
                )}
              </div>
              <div className="px-8 py-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
                <InfoRow icon={User} label="Name" value={application.user_name} />
                <InfoRow icon={Mail} label="Email" value={application.user_email} />
                <InfoRow icon={GraduationCap} label="Exam" value={
                  application.exam_title
                    ? `${application.exam_title}${application.exam_code ? ` [${application.exam_code}]` : ""}`
                    : null
                } />
                <InfoRow icon={Calendar} label="Diet" value={application.diet_title} />
                <InfoRow icon={MapPin} label="Center" value={application.center_name} />
                <InfoRow icon={Hash} label="Exam No" value={application.exam_no} />
              </div>
            </div>
          )}

          {/* Stats strip */}
          <div className="grid grid-cols-3 gap-4">
            <StatCard label="Total Courses" value={courses.length} color="gray" />
            <StatCard label="Registered" value={registered.length} color="emerald" />
            <StatCard label="Not Registered" value={unregistered.length} color="amber" />
          </div>

          {/* Course list */}
          {courses.length === 0 ? (
            <div className="bg-white border border-gray-100 rounded-[2.5rem] p-20 text-center">
              <BookOpen className="w-12 h-12 mx-auto text-gray-100 mb-4" />
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-300">
                No courses available for this exam
              </p>
            </div>
          ) : (
            <div className="bg-white border border-gray-100 rounded-[2.5rem] overflow-hidden shadow-sm">
              <div className="px-8 py-5 border-b border-gray-50 flex items-center justify-between">
                <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  Exam Courses
                </div>
                <div className="text-[10px] font-black text-gray-300 uppercase tracking-widest">
                  Click to toggle registration
                </div>
              </div>

              <div className="divide-y divide-gray-50">
                {courses.map((course) => {
                  const isToggling = toggling === course.id;
                  return (
                    <button
                      key={course.id}
                      onClick={() => toggle(course)}
                      disabled={toggling !== null}
                      className={`w-full text-left px-8 py-5 flex items-center gap-5 transition-all
                        hover:bg-gray-50 active:bg-gray-100
                        disabled:opacity-60 disabled:cursor-not-allowed
                        ${course.is_registered ? "bg-emerald-50/40 hover:bg-emerald-50" : ""}`}
                    >
                      <div className="flex-shrink-0">
                        {isToggling ? (
                          <Loader2 className="w-5 h-5 text-indigo-400 animate-spin" />
                        ) : course.is_registered ? (
                          <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                        ) : (
                          <Circle className="w-5 h-5 text-gray-300" />
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="text-[10px] font-black px-2 py-0.5 bg-gray-100 rounded text-gray-500 uppercase tracking-widest flex-shrink-0">
                            {course.code}
                          </span>
                          <span className="font-black text-gray-900 uppercase tracking-tight leading-tight">
                            {course.name}
                          </span>
                        </div>
                        {course.description && (
                          <p className="text-xs text-gray-400 mt-1 truncate">
                            {course.description}
                          </p>
                        )}
                      </div>

                      <div className="flex-shrink-0">
                        <span className={`text-[9px] font-black uppercase tracking-widest px-3 py-1 rounded-full border
                          ${course.is_registered
                            ? "bg-emerald-50 text-emerald-600 border-emerald-100"
                            : "bg-gray-50 text-gray-400 border-gray-100"
                          }`}
                        >
                          {course.is_registered ? "Registered" : "Not Registered"}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function InfoRow({
  icon: Icon,
  label,
  value,
}: {
  icon: any;
  label: string;
  value?: string | null;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-xl bg-gray-50 flex items-center justify-center flex-shrink-0 mt-0.5">
        <Icon className="w-3.5 h-3.5 text-gray-400" />
      </div>
      <div className="min-w-0">
        <div className="text-[9px] font-black text-gray-300 uppercase tracking-widest">
          {label}
        </div>
        <div className="text-sm font-bold text-gray-800 mt-0.5 truncate">
          {value ?? <span className="text-gray-300">—</span>}
        </div>
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: "gray" | "emerald" | "amber";
}) {
  const colors = {
    gray:    { num: "text-gray-900",    label: "text-gray-400"   },
    emerald: { num: "text-emerald-600", label: "text-emerald-400" },
    amber:   { num: "text-amber-600",   label: "text-amber-400"  },
  };
  const c = colors[color];
  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm text-center">
      <div className={`text-3xl font-black ${c.num}`}>{value}</div>
      <div className={`text-[9px] font-black uppercase tracking-widest mt-1 ${c.label}`}>
        {label}
      </div>
    </div>
  );
}
